/**
 * useDraftSession — Prototype A (canvas bake-off) client for the shared
 * chat↔page working draft.
 *
 * Read model: the last known session is mirrored to localStorage and read back
 * SYNCHRONOUSLY on mount, so the document is on screen in the first paint;
 * the server fetch then trues it up (stale-while-revalidate). Without that
 * mirror every visit rendered the studio's empty state for one round-trip
 * before the document appeared — the draft looked like it had to be "loaded"
 * each time, when it had merely never been cached. Refetch also on window
 * focus and on a 20s interval while the tab is visible (plain polling — a
 * single-user document doesn't justify websockets).
 * Write model: PATCH with the rev we last saw (optimistic concurrency). A 409
 * surfaces as `conflict` for the UI to resolve ("take theirs / keep mine") —
 * the standard optimistic-locking UX.
 *
 * PROTOTYPE: remove if Prototype B wins.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { draftSessionsAPI } from '../services/api';

const POLL_MS = 20000;

// ── Local mirror of the last seen session ────────────────────────────────
// localStorage, not IndexedDB (which this app also has, in draftCacheService):
// only a synchronous read can put the document in the FIRST paint. An async
// store would just shorten the empty flash instead of removing it.
const CACHE_PREFIX = 'cs_draft_session:';
// Keep a handful of companies warm without threatening the ~5MB origin quota;
// a minutes document is tens to a few hundred KB.
const MAX_CACHED = 6;

const cacheKeyFor = (companyCin, kind) => (companyCin ? `${CACHE_PREFIX}${kind}:${String(companyCin).toUpperCase()}` : '');

const readCache = (key) => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Guard against a half-written or schema-drifted entry: a session without
    // a rev can't participate in optimistic concurrency, so treat it as absent
    // rather than seeding the UI with something it can't safely PATCH.
    return parsed && parsed._id && Number.isFinite(parsed.rev) ? parsed : null;
  } catch { return null; }
};

// Drop the least recently written entries until we're back under the cap.
const pruneCache = () => {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX));
    if (keys.length <= MAX_CACHED) return;
    const stamped = keys.map((k) => {
      let at = 0;
      try { at = JSON.parse(localStorage.getItem(k))?._cachedAt || 0; } catch { /* unparseable → evict first */ }
      return { k, at };
    }).sort((a, b) => a.at - b.at);
    stamped.slice(0, stamped.length - MAX_CACHED).forEach(({ k }) => localStorage.removeItem(k));
  } catch { /* best-effort */ }
};

const writeCache = (key, session) => {
  if (!key) return;
  try {
    if (!session) { localStorage.removeItem(key); return; }
    localStorage.setItem(key, JSON.stringify({ ...session, _cachedAt: Date.now() }));
    pruneCache();
  } catch {
    // Quota exhausted (or storage blocked): make room and retry once. Failing
    // outright is fine — the cache is an accelerator, never the source of truth.
    try { pruneCache(); localStorage.setItem(key, JSON.stringify({ ...session, _cachedAt: Date.now() })); } catch { /* give up */ }
  }
};

export function useDraftSession(companyCin, kind = 'minutes') {
  const cacheKey = cacheKeyFor(companyCin, kind);
  // Seeded from the local mirror, so the document is present in the very first
  // render rather than one round-trip later.
  const [session, setSessionState] = useState(() => readCache(cacheKey));
  const [loading, setLoading] = useState(false);
  // Whether the SERVER has answered for the current company yet. The studios
  // need this to tell "there is genuinely no draft" apart from "we haven't
  // looked yet" — rendering the empty state during the second is what made an
  // existing draft look momentarily missing.
  const [hydrated, setHydrated] = useState(false);
  const [conflict, setConflict] = useState(null); // { server } when a PATCH hit 409
  const sessionRef = useRef(null);

  // Every write to session state mirrors locally, so the next mount is instant.
  const setSession = useCallback((next) => {
    sessionRef.current = next; // sync — callers read it back in the same tick
    setSessionState(next);
    writeCache(cacheKeyFor(next?.companyCin || companyCin, next?.kind || kind), next);
  }, [companyCin, kind]);

  useEffect(() => { sessionRef.current = session; }, [session]);

  // Company (or kind) switched: swap to THAT company's cached document during
  // render, before anything commits. Waiting for the effect would paint the
  // previous company's document into the new company's studio for a frame.
  const prevKeyRef = useRef(cacheKey);
  if (prevKeyRef.current !== cacheKey) {
    prevKeyRef.current = cacheKey;
    const cached = readCache(cacheKey);
    sessionRef.current = cached;
    setSessionState(cached);
    setHydrated(false);
  }

  const refresh = useCallback(async () => {
    if (!companyCin) return null;
    try {
      const res = await draftSessionsAPI.getActive(companyCin, kind);
      const next = res?.data?.session || null;
      // Only swap state when the server actually moved — keeps contentEditable
      // and form state stable across quiet polls.
      const cur = sessionRef.current;
      if ((next && (!cur || next.rev !== cur.rev || next._id !== cur._id)) || (!next && cur)) {
        setSession(next);
      }
      return next;
    } catch { return null; }
  }, [companyCin, kind, setSession]);

  // Initial load + reload on company switch. The cached document is already on
  // screen; this reconciles it (and is the only thing that can prove a company
  // genuinely has no draft).
  useEffect(() => {
    if (!companyCin) { setSession(null); setHydrated(true); return undefined; }
    let alive = true;
    setLoading(true);
    draftSessionsAPI.getActive(companyCin, kind)
      .then((res) => {
        if (!alive) return;
        const next = res?.data?.session || null;
        const cur = sessionRef.current;
        // Same rev as the cached copy → nothing changed; leave the object
        // identity alone so the studios' `[session]` effects stay quiet.
        if (next && cur && next._id === cur._id && next.rev === cur.rev) return;
        setSession(next);
      })
      .catch(() => { /* offline: the cached document stands */ })
      .finally(() => { if (alive) { setLoading(false); setHydrated(true); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCin, kind]);

  // Focus refetch + visible-tab polling.
  useEffect(() => {
    if (!companyCin) return undefined;
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(t); };
  }, [companyCin, refresh]);

  /**
   * Write fields to the session (creates it when none exists yet). On a rev
   * conflict, sets `conflict` and returns { conflict: server } instead.
   *
   * `baseRev` overrides the rev this write claims to be based on. A document
   * write must pass the rev actually RENDERED on screen, not the newest one
   * known: since the canvas is seeded from the local cache, the two can differ
   * for one round-trip, and editing text from rev 8 while claiming rev 9 would
   * silently overwrite whatever rev 9 changed. Passing the rendered rev turns
   * that into the 409 the conflict UI already knows how to resolve.
   */
  const save = useCallback(async (fields, { baseRev } = {}) => {
    const cur = sessionRef.current;
    try {
      if (!cur) {
        const res = await draftSessionsAPI.create({ kind, companyCin, ...fields });
        const created = res?.data?.session || null;
        setSession(created);
        return { session: created };
      }
      const res = await draftSessionsAPI.patch(cur._id, { baseRev: Number.isFinite(baseRev) ? baseRev : cur.rev, ...fields });
      const updated = res?.data?.session || null;
      setSession(updated);
      return { session: updated };
    } catch (err) {
      const server = err?.details?.session;
      if (err?.status === 409 && server) {
        setConflict({ server });
        return { conflict: server };
      }
      throw err;
    }
  }, [companyCin, kind, setSession]);

  /** Conflict resolution: adopt the server's version wholesale. */
  const takeTheirs = useCallback(() => {
    if (conflict?.server) setSession(conflict.server);
    setConflict(null);
  }, [conflict, setSession]);

  /** Conflict resolution: overwrite the server with `fields`, based on ITS rev. */
  const keepMine = useCallback(async (fields) => {
    const server = conflict?.server;
    setConflict(null);
    if (!server) return null;
    try {
      const res = await draftSessionsAPI.patch(server._id, { baseRev: server.rev, ...fields });
      const updated = res?.data?.session || null;
      setSession(updated);
      return { session: updated };
    } catch (err) {
      const s = err?.details?.session;
      if (err?.status === 409 && s) { setConflict({ server: s }); return { conflict: s }; }
      throw err;
    }
  }, [conflict, setSession]);

  return { session, loading, hydrated, conflict, refresh, save, takeTheirs, keepMine };
}

export default useDraftSession;
